// Talks to the SnugFit backend APIs used by the web based 3D scanner.

// orders from any Order-N-Scan apps always use the same "weblink-user" practice.
const PRACTICE_ID = "weblink-user";

// POST /api/decrypt — decrypt the order data carried in the universal link.
// Returns the parsed order.
export async function decryptOrder({ orderData, labId }) {
  const apiUrl = process.env.REACT_APP_SNUGFIT_API_DECRYPT || "/api/decrypt";
  const response = await fetch(apiUrl, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ orderData, labId }),
  });

  const text = await response.text();
  let json;
  try {
    json = JSON.parse(text);
  } catch {
    throw new Error(`(${response.status}) ${text}`);
  }
  if (!response.ok) {
    // 400, 403, 500, etc. — surface the API's error message.
    throw new Error(`(${response.status}) ${json.message}`);
  }
  return json;
}

// POST /api/job — create a new vision job.
// Returns the parsed response.
export async function createJob({
  relatedLabId,
  scanType,
  webOrderId,
  webOrderUserName,
}) {
  // Env var overrides the endpoint for local testing; defaults to the
  // same-origin relative path used in production (via CloudFront).
  const apiUrl = process.env.REACT_APP_SNUGFIT_API_JOB || "/api/job";
  const response = await fetch(apiUrl, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      practiceId: PRACTICE_ID,
      relatedLabId,
      scanType,
      webOrderId,
      webOrderUserName,
    }),
  });

  const text = await response.text();
  let json;
  try {
    json = JSON.parse(text);
  } catch {
    throw new Error(`(${response.status}) ${text}`);
  }
  const defaultErr =
    "Failed to create the processing job, please try again later.";
  if (!response.ok) {
    throw new Error(`(${response.status}) ${json.message || defaultErr}`);
  }
  // the API reports business failures in the body (e.g. unknown lab) with a
  // non-"success" status, often still on HTTP 200.
  if (json.status !== "success") {
    throw new Error(json.error || defaultErr);
  }
  return json;
}

// GET /api/job — query the current status/progress of a job.
// Returns the parsed response.
export async function getJobProgress(jobId) {
  const base = process.env.REACT_APP_SNUGFIT_API_JOB || "/api/job";
  const url = `${base}?id=${encodeURIComponent(jobId)}`;
  const response = await fetch(url, { method: "GET" });

  const text = await response.text();
  let json;
  try {
    json = JSON.parse(text);
  } catch {
    throw new Error(`(${response.status}) ${text}`);
  }
  const defaultErr = "Failed to get the processing status.";
  if (!response.ok) {
    throw new Error(`(${response.status}) ${json.message || defaultErr}`);
  }
  // an API-level failure (e.g. unknown job) is reported with a non-"success"
  // status; the job's own state lives in `jobStatus`.
  if (json.status !== "success") {
    throw new Error(json.error || defaultErr);
  }
  return json;
}

// for the web-based scanner, the "cameraType" field for the metrics job is
// always "rear", no matter which camera the user actually used.
const METRICS_CAMERA_TYPE = "rear";

// POST /api/metrics — create a new metrics job measuring a generated model.
// Returns the parsed response ({ jobId, dataUrl, ... }); note its jobId is the
// metrics job's own id, unrelated to the vision job's.
export async function createMetricsJob({
  relatedLabId,
  scanType,
  webOrderId,
  webOrderUserName,
  dataUrl,
  dataFilename,
}) {
  const apiUrl = process.env.REACT_APP_SNUGFIT_API_METRICS || "/api/metrics";
  const response = await fetch(apiUrl, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      practiceId: PRACTICE_ID,
      relatedLabId,
      scanType,
      webOrderId,
      webOrderUserName,
      dataUrl,
      dataFilename,
      cameraType: METRICS_CAMERA_TYPE,
    }),
  });

  const text = await response.text();
  let json;
  try {
    json = JSON.parse(text);
  } catch {
    throw new Error(`(${response.status}) ${text}`);
  }
  const defaultErr =
    "Failed to create the measurement job, please try again later.";
  if (!response.ok) {
    throw new Error(`(${response.status}) ${json.message || defaultErr}`);
  }
  // business failures are reported with a non-"success" status (often HTTP 200).
  if (json.status !== "success") {
    throw new Error(json.error || defaultErr);
  }
  return json;
}

// GET /api/metrics — query the current status/progress of a metrics job.
// Returns the parsed response.
export async function getMetricsProgress(jobId) {
  const base = process.env.REACT_APP_SNUGFIT_API_METRICS || "/api/metrics";
  const url = `${base}?id=${encodeURIComponent(jobId)}`;
  const response = await fetch(url, { method: "GET" });

  const text = await response.text();
  let json;
  try {
    json = JSON.parse(text);
  } catch {
    throw new Error(`(${response.status}) ${text}`);
  }
  const defaultErr = "Failed to get the measurement status.";
  if (!response.ok) {
    throw new Error(`(${response.status}) ${json.message || defaultErr}`);
  }
  // an API-level failure (e.g. unknown job) is reported with a non-"success"
  // status; the job's own state lives in `jobStatus`.
  if (json.status !== "success") {
    throw new Error(json.error || defaultErr);
  }
  return json;
}

// the cleaned model file a metrics job produces
export const METRICS_MODEL_FILENAME = "model.stl";

// the name (without extension) the uploaded model files of a scan are saved
// as: "Left Shoe" -> "left-shoe-vs".
export function modelFileName(title) {
  return `${title.toLowerCase().replace(/\s+/g, "-")}-vs`;
}

// POST /api/order — place the new web order and save data (upload to
// customers' predefined cloud storage).
// Returns the parsed response.
export async function createOrder({
  relatedLabId,
  scanTypes,
  webOrderId,
  webOrderUserName,
  webOrderDetails,
  extraData,
  dataUrls,
  modelNames,
  image,
  metricsApiUsed,
  metricsDataUrls,
  metricsModelNames,
}) {
  const apiUrl = process.env.REACT_APP_SNUGFIT_API_ORDER || "/api/order";
  const body = {
    practiceId: PRACTICE_ID,
    relatedLabId,
    scanTypes,
    webOrderId,
    webOrderUserName,
    dataUrls,
    modelNames,
  };
  // optional fields
  if (webOrderDetails) body.webOrderDetails = webOrderDetails;
  if (extraData) body.extraData = extraData;
  if (image) body.image = image;
  if (metricsApiUsed) body.metricsApiUsed = metricsApiUsed;
  if (metricsDataUrls) body.metricsDataUrls = metricsDataUrls;
  if (metricsModelNames) body.metricsModelNames = metricsModelNames;

  const response = await fetch(apiUrl, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

  const text = await response.text();
  let json;
  try {
    json = JSON.parse(text);
  } catch {
    throw new Error(`(${response.status}) ${text}`);
  }
  const defaultErr = "Failed to upload the data, please try again later.";
  if (!response.ok) {
    throw new Error(`(${response.status}) ${json.message || defaultErr}`);
  }
  // business failures are reported with a non-"success" status (often HTTP 200).
  if (json.status !== "success") {
    throw new Error(json.error || defaultErr);
  }
  return json;
}

// PUT the zip blob directly to the presigned S3 URL.
export async function uploadToPresignedUrl(presignedUrl, blob) {
  const response = await fetch(presignedUrl, {
    method: "PUT",
    headers: { "Content-Type": "application/octet-stream" },
    body: blob,
  });
  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(`(${response.status}) ${text}`);
  }
}
